import { gql, useMutation, useQuery } from '@apollo/client';
import formatTimeAgo from '@orn/shared-web/utils/datetime/formatTimeAgo';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { FeatureGateAudience } from '../../__generated__/globalTypes';
import Button from '../../components/Button';
import DangerConfirmDialog from '../../components/DangerConfirmDialog';
import Header from '../../components/Header';
import Table from '../../components/Table';
import { useToast } from '../../components/Toast';
import { featureGateEditPath, featureGateNewPath, PATHS } from '../../paths';

import { findUnconfiguredGates, partitionGatesByCodeUsage } from './gateActivity';
import { summarizeRule } from './ruleSerialization';
import { parseRuleTree } from './ruleTree';

import type {
  AdminListFeatureGatesQuery,
  AdminToggleFeatureGateMutation,
  AdminToggleFeatureGateMutationVariables,
} from './__generated__/FeatureGatesScene.gql';

const ADMIN_LIST_FEATURE_GATES = gql`
  query AdminListFeatureGates {
    adminListFeatureGates {
      id
      name
      description
      isEnabled
      ruleTree
      audience
      defaultWhenError
      lastModifiedByUserEmail
      createdAt
      updatedAt
    }
  }
`;

const ADMIN_TOGGLE_FEATURE_GATE = gql`
  mutation AdminToggleFeatureGate($id: ID!, $isEnabled: Boolean!) {
    adminToggleFeatureGate(id: $id, isEnabled: $isEnabled) {
      id
      isEnabled
      updatedAt
      lastModifiedByUserEmail
    }
  }
`;

type FeatureGateRow = AdminListFeatureGatesQuery['adminListFeatureGates'][number];

const AUDIENCE_BADGE: Record<FeatureGateAudience, { label: string; tone: 'neutral' | 'brand' | 'muted' }> = {
  [FeatureGateAudience.ServerOnly]: { label: 'SERVER', tone: 'muted' },
  [FeatureGateAudience.WebClient]: { label: 'USER', tone: 'neutral' },
  [FeatureGateAudience.MopsWebClient]: { label: 'ADMIN', tone: 'brand' },
  [FeatureGateAudience.AllClients]: { label: 'BOTH', tone: 'brand' },
};

/**
 * Admin list view for every feature gate. Operator-tuned dense table with
 * inline `j` / `k` row navigation, Enter to open the edit form, and a
 * sticky `+` button that routes to the new-gate flow. Disabling an
 * enabled gate inline (the toggle in the row) opens the typed-confirmation
 * danger dialog so a misclick can never silently kill a gate; re-enabling
 * does not require typed confirmation.
 *
 * Gates whose name is missing from the `FeatureGate` registry in
 * `@orn/global` are annexed into a separate "Inactive" section below the
 * main table — no code path can evaluate them. j/k navigation spans both
 * sections as one list.
 *
 * The reverse case — a name in the registry with no `feature_gates` row yet
 * — is surfaced in an "Unconfigured" section whose rows deep-link into the
 * new-gate form with the name pre-filled. Those rows are informational (no
 * toggle, no rule), so they sit outside the j/k nav order.
 */
const FeatureGatesScene = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { data, loading } = useQuery<AdminListFeatureGatesQuery>(ADMIN_LIST_FEATURE_GATES, {
    fetchPolicy: 'cache-and-network',
  });
  const [toggleGate] = useMutation<AdminToggleFeatureGateMutation, AdminToggleFeatureGateMutationVariables>(
    ADMIN_TOGGLE_FEATURE_GATE
  );

  // Stable references so the keydown effect below isn't re-bound on every
  // render — Apollo gives us a fresh array each tick when the underlying
  // entity hasn't changed. `gates` is the flattened nav order (active rows
  // first, then inactive) so a single focusedIndex spans both tables.
  const { active: activeGates, inactive: inactiveGates } = React.useMemo(
    () => partitionGatesByCodeUsage(data?.adminListFeatureGates ?? []),
    [data?.adminListFeatureGates]
  );
  const gates = React.useMemo(() => [...activeGates, ...inactiveGates], [activeGates, inactiveGates]);
  // Only compute the unconfigured set once the query has resolved — deriving
  // it from an undefined result would flag the entire registry as unconfigured
  // and flash a full "create these" list during the initial load.
  const unconfiguredGates = React.useMemo(
    () => (data?.adminListFeatureGates ? findUnconfiguredGates(data.adminListFeatureGates) : []),
    [data?.adminListFeatureGates]
  );
  const [focusedIndex, setFocusedIndex] = React.useState<number>(0);
  const [pendingDisable, setPendingDisable] = React.useState<FeatureGateRow | null>(null);

  // Keep the focused row in range when the result set shrinks (e.g. after
  // a delete from the edit page). Clamp to the last row rather than 0 so
  // operators don't lose place after a routine remove.
  React.useEffect(() => {
    if (focusedIndex >= gates.length && gates.length > 0) {
      setFocusedIndex(gates.length - 1);
    }
  }, [gates.length, focusedIndex]);

  const openEdit = React.useCallback(
    (gate: FeatureGateRow) => {
      void navigate(featureGateEditPath(gate.id));
    },
    [navigate]
  );

  const openConfigure = React.useCallback(
    (name: string) => {
      void navigate(featureGateNewPath(name));
    },
    [navigate]
  );

  const requestEnable = async (gate: FeatureGateRow) => {
    try {
      await toggleGate({ variables: { id: gate.id, isEnabled: true } });
      showToast({ type: 'success', message: `Enabled ${gate.name}.` });
    } catch {
      showToast({ type: 'error', message: `Failed to enable ${gate.name}.` });
    }
  };

  const requestDisable = (gate: FeatureGateRow) => {
    // Defer the mutation until the typed-confirmation dialog approves.
    // The toggle visual state below reads from the row's `isEnabled`, so
    // we don't need an "optimistic" flip — cancel just closes the dialog.
    setPendingDisable(gate);
  };

  const performDisable = async () => {
    if (!pendingDisable) {
      return;
    }
    try {
      await toggleGate({ variables: { id: pendingDisable.id, isEnabled: false } });
      showToast({ type: 'success', message: `Disabled ${pendingDisable.name}.` });
    } catch (err) {
      showToast({ type: 'error', message: `Failed to disable ${pendingDisable.name}.` });
      // Re-throw the original error so the dialog stays open and the
      // Apollo errorLink/Sentry context is preserved. A synthetic Error
      // here would discard the GraphQL stack and break debugging.
      throw err;
    }
  };

  // Global keyboard shortcuts — ignored when focus is in a form field so
  // typing 'j' into an input doesn't navigate the table.
  //
  // - `j` / `k`: row navigation
  // - `Enter`: open the focused gate's edit page
  // - `n`: jump to the new-gate form (matches `+ New gate` button)
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      if (e.key === 'n') {
        e.preventDefault();
        void navigate(PATHS.FEATURE_GATE_NEW);
        return;
      }
      if (gates.length === 0) {
        return;
      }
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((i) => Math.min(i + 1, gates.length - 1));
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        const gate = gates[focusedIndex];
        if (gate) {
          openEdit(gate);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [focusedIndex, gates, navigate, openEdit]);

  const hasSideBuckets = inactiveGates.length > 0 || unconfiguredGates.length > 0;
  const countSummary = hasSideBuckets
    ? [
        `${activeGates.length} active`,
        inactiveGates.length > 0 ? `${inactiveGates.length} inactive` : null,
        unconfiguredGates.length > 0 ? `${unconfiguredGates.length} unconfigured` : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : `${gates.length} ${gates.length === 1 ? 'gate' : 'gates'}`;

  return (
    <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-4 px-6 py-5">
        <div className="flex items-baseline justify-between">
          <h1 className="text-sm font-semibold uppercase tracking-widest">Feature gates</h1>
          <div className="flex items-center gap-2">
            <span className="text-fg-tertiary text-xs">{countSummary}</span>
            <Button
              size="sm"
              color="primary"
              onClick={() => navigate(PATHS.FEATURE_GATE_NEW)}
              aria-label="New feature gate"
            >
              + New gate
            </Button>
          </div>
        </div>

        <p className="text-fg-tertiary text-xs">
          Use <kbd className="border-border-primary text-fg-secondary border px-1 font-mono">j</kbd> /{' '}
          <kbd className="border-border-primary text-fg-secondary border px-1 font-mono">k</kbd> (or arrow keys) to
          move, <kbd className="border-border-primary text-fg-secondary border px-1 font-mono">Enter</kbd> to open,{' '}
          <kbd className="border-border-primary text-fg-secondary border px-1 font-mono">n</kbd> for a new gate.
          Disabling an enabled gate requires typed confirmation.
        </p>

        {loading && gates.length === 0 && unconfiguredGates.length === 0 ? (
          <span className="text-fg-secondary text-xs">Loading…</span>
        ) : gates.length === 0 && unconfiguredGates.length === 0 ? (
          <span className="text-fg-tertiary text-xs italic">
            No feature gates yet. Use the + button or `git mv` your codenames here when you're ready to roll.
          </span>
        ) : (
          <>
            {activeGates.length > 0 && (
              <GatesTable
                gates={activeGates}
                indexOffset={0}
                sticky
                focusedIndex={focusedIndex}
                onFocusIndex={setFocusedIndex}
                onOpen={openEdit}
                onToggle={(gate, next) => (next ? void requestEnable(gate) : requestDisable(gate))}
              />
            )}
            {inactiveGates.length > 0 && (
              <section className="flex flex-col gap-2 pt-4">
                <h2 className="text-fg-tertiary text-xs font-semibold uppercase tracking-widest">
                  Inactive — not referenced in code
                </h2>
                <p className="text-fg-tertiary text-xs">
                  These gate rows have no matching entry in the <span className="font-mono">FeatureGate</span> registry
                  (<span className="font-mono">@orn/global</span>), so no code path can evaluate them — either a retired
                  gate whose row was never deleted, or a name typo'd at creation.
                </p>
                <GatesTable
                  gates={inactiveGates}
                  indexOffset={activeGates.length}
                  focusedIndex={focusedIndex}
                  onFocusIndex={setFocusedIndex}
                  onOpen={openEdit}
                  onToggle={(gate, next) => (next ? void requestEnable(gate) : requestDisable(gate))}
                  inactive
                />
              </section>
            )}
            {unconfiguredGates.length > 0 && (
              <section className="flex flex-col gap-2 pt-4">
                <h2 className="text-fg-tertiary text-xs font-semibold uppercase tracking-widest">
                  Unconfigured — in code, no gate row yet
                </h2>
                <p className="text-fg-tertiary text-xs">
                  These names are referenced in the <span className="font-mono">FeatureGate</span> registry (
                  <span className="font-mono">@orn/global</span>) but have no{' '}
                  <span className="font-mono">feature_gates</span> row, so in staging and prod they fail closed (off)
                  until you create one. Configure a gate to open the new-gate form with its name pre-filled.
                </p>
                <UnconfiguredGatesTable gates={unconfiguredGates} onConfigure={openConfigure} />
              </section>
            )}
          </>
        )}
      </main>

      <DangerConfirmDialog
        isOpen={pendingDisable !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDisable(null);
          }
        }}
        confirmValue={pendingDisable?.name ?? ''}
        title="Disable feature gate"
        body={
          <>
            Disabling this gate immediately removes access for every user it currently passes for. Server-side callers
            will fall back to the gate's <span className="font-mono">defaultValue</span>. To proceed, type the gate
            name.
          </>
        }
        confirmLabel="Disable"
        onConfirm={performDisable}
      />
    </div>
  );
};

interface IGatesTableProps {
  gates: FeatureGateRow[];
  /** Position of this table's first row within the flattened j/k nav order. */
  indexOffset: number;
  sticky?: boolean;
  inactive?: boolean;
  focusedIndex: number;
  onFocusIndex: (index: number) => void;
  onOpen: (gate: FeatureGateRow) => void;
  onToggle: (gate: FeatureGateRow, next: boolean) => void;
}

const GatesTable = ({
  gates,
  indexOffset,
  sticky = false,
  inactive = false,
  focusedIndex,
  onFocusIndex,
  onOpen,
  onToggle,
}: IGatesTableProps) => (
  <Table>
    <Table.Header sticky={sticky}>
      <Table.Row>
        <Table.HeaderCell>Name</Table.HeaderCell>
        <Table.HeaderCell>On</Table.HeaderCell>
        <Table.HeaderCell>Rule</Table.HeaderCell>
        <Table.HeaderCell>Audience</Table.HeaderCell>
        <Table.HeaderCell>Updated</Table.HeaderCell>
        <Table.HeaderCell>Last edited by</Table.HeaderCell>
      </Table.Row>
    </Table.Header>
    <Table.Body>
      {gates.map((gate, idx) => (
        <FeatureGateRowView
          key={gate.id}
          gate={gate}
          inactive={inactive}
          isFocused={indexOffset + idx === focusedIndex}
          onFocus={() => onFocusIndex(indexOffset + idx)}
          onOpen={() => onOpen(gate)}
          onToggle={(next) => onToggle(gate, next)}
        />
      ))}
    </Table.Body>
  </Table>
);

interface IUnconfiguredGatesTableProps {
  gates: string[];
  onConfigure: (name: string) => void;
}

/**
 * Read-only companion table for gates that exist in code but have no DB row.
 * No toggle or rule column — these gates aren't evaluable yet — just the name
 * and a Configure action that deep-links into the new-gate form with the name
 * pre-filled.
 */
const UnconfiguredGatesTable = ({ gates, onConfigure }: IUnconfiguredGatesTableProps) => (
  <Table>
    <Table.Header>
      <Table.Row>
        <Table.HeaderCell>Name</Table.HeaderCell>
        <Table.HeaderCell />
      </Table.Row>
    </Table.Header>
    <Table.Body>
      {gates.map((name) => (
        <Table.Row key={name}>
          <Table.Cell mono>{name}</Table.Cell>
          <Table.Cell>
            <Button size="xs" color="secondary" onClick={() => onConfigure(name)} aria-label={`Configure ${name}`}>
              Configure
            </Button>
          </Table.Cell>
        </Table.Row>
      ))}
    </Table.Body>
  </Table>
);

interface IRowProps {
  gate: FeatureGateRow;
  inactive: boolean;
  isFocused: boolean;
  onFocus: () => void;
  onOpen: () => void;
  onToggle: (next: boolean) => void;
}

const FeatureGateRowView = ({ gate, inactive, isFocused, onFocus, onOpen, onToggle }: IRowProps) => {
  const ruleSummary = React.useMemo(() => {
    const parsed = parseRuleTree(gate.ruleTree);
    return parsed ? summarizeRule(parsed) : 'Custom rule';
  }, [gate.ruleTree]);
  const badge = AUDIENCE_BADGE[gate.audience];

  return (
    <Table.Row
      onClick={onOpen}
      onMouseEnter={onFocus}
      tabIndex={-1}
      aria-selected={isFocused}
      className={isFocused ? 'bg-fg-brand-primary/10 hover:bg-fg-brand-primary/15 cursor-pointer' : 'cursor-pointer'}
    >
      <Table.Cell mono muted={inactive}>
        {/* Reserved gutter for the focus chevron — keeps name alignment stable
            so toggling focus doesn't shift the table content. */}
        <span aria-hidden="true" className="text-fg-brand-primary mr-1.5 inline-block w-3 text-center">
          {isFocused ? '▸' : ''}
        </span>
        {gate.name}
      </Table.Cell>
      <Table.Cell>
        <button
          type="button"
          aria-label={gate.isEnabled ? `Disable ${gate.name}` : `Enable ${gate.name}`}
          aria-pressed={gate.isEnabled}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(!gate.isEnabled);
          }}
          className={
            'focus-visible:outline-border-brand block size-4 cursor-pointer border transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 ' +
            (gate.isEnabled
              ? 'bg-fg-success-primary border-fg-success-primary'
              : 'bg-bg-secondary border-border-primary')
          }
        />
      </Table.Cell>
      <Table.Cell muted>{ruleSummary}</Table.Cell>
      <Table.Cell>
        <span
          className={
            'border px-1.5 py-0.5 font-mono text-xs uppercase tracking-wider ' +
            (badge.tone === 'brand'
              ? 'border-fg-brand-primary text-fg-brand-primary'
              : badge.tone === 'muted'
                ? 'border-border-primary text-fg-tertiary'
                : 'border-border-primary text-fg-secondary')
          }
        >
          {badge.label}
        </span>
      </Table.Cell>
      <Table.Cell muted>{formatTimeAgo(gate.updatedAt)}</Table.Cell>
      <Table.Cell mono muted>
        {gate.lastModifiedByUserEmail ?? '—'}
      </Table.Cell>
    </Table.Row>
  );
};

export default FeatureGatesScene;
